import Flux from 'flux';
import FluxUtils from 'flux/utils';
import Immutable from 'immutable';

import type { ActionType, PaginatedActionsType } from 'sharedreact/actions/generic/actions';
import AllItemsConstants from 'sharedreact/constants/AllItemsConstants';
import UserPreferencesConstants from 'sharedreact/constants/UserPreferencesConstants';
import type { ItemsConstantsType } from 'sharedreact/constants/generic/Constants';
import { FilterMode } from 'sharedreact/constants/generic/Constants';
import Filter from 'sharedreact/data/Filter';
import GroupAndCount from 'sharedreact/data/GroupAndCount';
import SortOrder from 'sharedreact/data/SortOrder';
import type UpdateAction from 'sharedreact/data/UpdateAction';
import type ItemsState from 'sharedreact/stores/generic/ItemsState';
import type { ItemType } from 'sharedreact/stores/generic/ItemsState';
import { getInitialItemState } from 'sharedreact/stores/generic/ItemsState';
import type { RangeType } from 'sharedreact/stores/generic/Type';
import { compareValues } from 'sharedreact/utils/CompareSupport';
import FilterChecker from 'sharedreact/utils/FilterChecker';

const _windowSize: number = 50;

class PaginatedItemsStore<Item extends ItemType, ExtraData = null> extends FluxUtils.ReduceStore<ItemsState<Item | null, ExtraData>, ActionType> {
  Actions: PaginatedActionsType;
  Constants: ItemsConstantsType;
  _currentRange: RangeType;
  _allowSelectionModifiers: boolean;
  _removedIds: string[];
  _shouldSetEnsureRangeVisible: boolean;

  _isStoreActive: boolean;
  _isStoreDirty: boolean;

  _isFetching: boolean;

  _refetchCount: number;

  constructor (Dispatcher: Flux.Dispatcher<ActionType>, Actions: PaginatedActionsType, Constants: ItemsConstantsType, allowSelectionModifiers: boolean = false) {
    super(Dispatcher);

    this.Actions = Actions;
    this.Constants = Constants;

    this._currentRange = {from: 0, until: 0}; // currentRange is not part of the
                                              // state because we don't want to update the store
                                              // state when it changes. The state only needs to
                                              // change when the new data is received from the server.
    this._allowSelectionModifiers = allowSelectionModifiers;
    this._shouldSetEnsureRangeVisible = false;

    this._removedIds = [];

    this._isStoreActive = true;
    this._isStoreDirty = false;

    this._isFetching = false;

    this._refetchCount = 0;
  }

  isActive (): boolean {
    return this._isStoreActive;
  }

  getInitialSortOrder (): SortOrder {
    return new SortOrder({keyword: '', ascending: true});
  }

  getSortKeywords (): string[] {
    return [];
  }

  getSecondarySortKeyword (): string {
    return this.getInitialSortOrder().keyword;
  }

  getGroupingKeywords (): string[] {
    return [];
  }

  getFilterKeywords (): string[] {
    return [];
  }

  _getAllFilterKeywords (): string [] {
    return this.getFilterKeywords();
  }

  getStateNames (): {name: string, value: string}[] | null {
    return null;
  }

  getItemKeyForKeyword (keyword: string): string | null {
    return keyword;
  }

  override getInitialState (): ItemsState<Item | null, ExtraData> {
    return getInitialItemState<Item, ExtraData>(this.getInitialSortOrder());
  }

  getItems (): Immutable.List<Item | null> {
    return this.getState().items;
  }

  getCountPerGroup (): Immutable.List<GroupAndCount> {
    return this.getState().countPerGroup;
  }

  getFilter (): Filter {
    return this.getState().filter;
  }

  getFilterString (): string {
    return this.getState().filterString;
  }

  getAdvancedFilter (): Filter {
    return this.getState().advancedFilter;
  }

  getSortOrder (): SortOrder {
    return this.getState().sortOrder;
  }

  _getPreSortOrder (): string | SortOrder | null {
    return null;
  }

  getSelection (): Immutable.Set<string> {
    return this.getState().selection;
  }

  getSelectedItems (): Immutable.Set<Item> {
    return this.getSelection().map((id: string) => this.getItem(id)!);
  }

  getAllowMultiSelect (): boolean {
    return this.getState().allowMultiSelect;
  }

  getEnsureSelectionIsVisible (): boolean {
    return this.getState().ensureSelectionIsVisible;
  }

  getSelectionIndexes (): Immutable.Map<string, number> {
    return this.getState().selectionIndexes;
  }

  getItem (id: string): Item | undefined {
    return this.getState().mappedItems.get(id);
  }

  getName (id: string): string | undefined {
    const itemForId: Item | undefined = this.getItem(id);
    return itemForId ? itemForId.name : undefined;
  }

  getAllowSelectionsFromAllGroups (): boolean {
    return this.getState().allowSelectionsFromAllGroups;
  }

  getIsAdding (): boolean {
    return this.getState().isAdding;
  }

  isFetching () {
    return this.getState().isFetching || this._isFetching;
  }

  hasNotFetchedYet () {
    return this.getState().hasNotFetchedYet;
  }

  hasReceivedInitialData () {
    return this.getState().hasReceivedInitialData;
  }

  _getWindowSize (): number {
    return _windowSize;
  }

  _find (
    ids: Immutable.Set<string>,
    filter: Filter,
    advancedFilter: Filter,
    groupBy: SortOrder,
    sortOrder: SortOrder
  ): void {
    if (this.Actions.find != null) {
      this.Actions.find(ids, filter, advancedFilter, groupBy, sortOrder);
    }
  }

  _getAll (
    state: ItemsState<Item | null, ExtraData>,
    range: RangeType,
    filter: Filter,
    advancedFilter: Filter,
    groupBy: SortOrder,
    sortOrder: SortOrder
  ): void {
    this.Actions.fetch(range, filter, advancedFilter, groupBy, sortOrder);
  }

  _getRange (
    state: ItemsState<Item | null, ExtraData>,
    range: RangeType,
    filter: Filter,
    advancedFilter: Filter,
    sortOrder: SortOrder
  ): ItemsState<Item | null, ExtraData> {
    if (this._isStoreActive && !state.isFetching && !this._isFetching && range.until > range.from) {
      this._isFetching = true;
      state = state.set('isFetching', true)
        .set('hasNotFetchedYet', false);

      if (this.getGroupingKeywords().includes(sortOrder.keyword)) {
        this._getAll(state, range, filter, advancedFilter, sortOrder, sortOrder);
      } else {
        this._getAll(state, range, filter, advancedFilter, new SortOrder({keyword: '', ascending: true}), sortOrder);
      }
    }
    return state;
  }

  _removeMappedItemsThatAreNotInUseByOtherStores (state: ItemsState<Item | null, ExtraData>): ItemsState<Item | null, ExtraData> {
    let mappedItems: Immutable.Map<string, Item> = state.mappedItems;
    const mappedItemIdsToRemove: string[] = [];
    for (const mappedItem of mappedItems) {
      if (typeof mappedItem[0] === 'string') {
        const mappedItemId: string = mappedItem[0];
        if (!this._isItemInUse(mappedItemId)) {
          mappedItemIdsToRemove.push(mappedItemId);
        }
      }
    }

    for (const mappedIdToRemove of mappedItemIdsToRemove) {
      mappedItems = mappedItems.delete(mappedIdToRemove);
    }

    return state.set('mappedItems', mappedItems);
  }

  _refresh (state: ItemsState<Item | null, ExtraData>): ItemsState<Item | null, ExtraData> {
    state = state.set('hasReceivedInitialData', false);

    if (!state.isFetching && !this._isFetching && state.hasNotFetchedYet) {
      return state;
    }
      // refresh needed: fetch the items and clear the selection
    state = this._getRange(
      state,
      this._currentRange,
      state.filter,
      state.advancedFilter,
      state.sortOrder
    );
    return this._removeMappedItemsThatAreNotInUseByOtherStores(
      state.set('items', state.items.clear())
    ).set('selection', state.selection.clear());
  }

  _setFilter (state: ItemsState<Item | null, ExtraData>, filter: Filter, filterString: string): ItemsState<Item | null, ExtraData> {
    // filter changed, fetch the items and clear the selection
    state = state.set('hasNotFetchedYet', true);
    state = this._getRange(
      state,
      this._currentRange,
      filter,
      state.advancedFilter,
      state.sortOrder
    );

    return this._removeMappedItemsThatAreNotInUseByOtherStores(
      state
        .set('items', state.items.map(() => null))
        .set('filter', filter)
        .set('filterString', filterString)
    );
  }

  _setAdvancedFilter (state: ItemsState<Item | null, ExtraData>, advancedFilter: Filter): ItemsState<Item | null, ExtraData> {
    if (!Immutable.is(state.advancedFilter, advancedFilter)) {
      // advanced filter changed, fetch the items and clear the selection
      state = state.set('hasNotFetchedYet', true);
      state = this._getRange(
        state,
        this._currentRange,
        state.filter,
        advancedFilter,
        state.sortOrder
      );
      return this._removeMappedItemsThatAreNotInUseByOtherStores(state.set('items', state.items.map(() => null)).set('advancedFilter', advancedFilter))
        .set('selection', state.selection.clear());
    }

    return state;
  }

  // If a subclass uses another field for the selection instead of id, then that class should
  // provide it's own _selectionExistsInItems() method, filtering for the correct member.
  _selectionExistsInItems (id: string, items: Immutable.List<Item | null>) {
    const index = this._findIndexForId(items, id);
    return index !== -1;
  }

  _createWindowRange (range: RangeType): RangeType {
    return {
      from: Math.floor(range.from / this._getWindowSize()) * this._getWindowSize(),
      until: Math.ceil(range.until / this._getWindowSize()) * this._getWindowSize() + ((range.until % this._getWindowSize()) >= Math.max(10, this._getWindowSize() - 10) || (range.until % this._getWindowSize()) === 0 ? this._getWindowSize() : 0)
    };
  }

  _calculateRangeToGet (items: Immutable.List<Item | null>, range: RangeType, isInitialFetch: boolean): RangeType | null {
    if (isInitialFetch || this._isStoreDirty) {
      return {from: range.from, until: range.until};
    } else {
      const newRange: RangeType = {from: range.from, until: Math.min(range.until, items.size)};

      const inRangeList: Immutable.List<Item | null> = items.slice(newRange.from, newRange.until);
      const start: number = inRangeList.findIndex((item) => item == null);
      if (start !== -1) {
        const end: number = inRangeList.slice(start, -1).findIndex((item) => item != null);

        if (end !== -1) {
          return {from: newRange.from + start, until: newRange.from + start + end};
        }

        return {from: newRange.from + start, until: newRange.until};
      }

      if (newRange.until <= items.size) {
        return null;
      } else {
        return {from: newRange.from, until: newRange.until};
      }
    }
  }

  _updateRange (state: ItemsState<Item | null, ExtraData>, range: RangeType, isInitialFetch: boolean): ItemsState<Item | null, ExtraData> {
    // create Window Range
    const newRange: RangeType = this._createWindowRange(range);

    if (!state.isFetching && !this._isFetching) {
      const rangeToGet: RangeType | null = this._calculateRangeToGet(state.items, newRange, isInitialFetch);

      if (rangeToGet && rangeToGet.until > rangeToGet.from) {
        state = this._getRange(
          state,
          rangeToGet,
          state.filter,
          state.advancedFilter,
          state.sortOrder
        );
      }
    }

    this._currentRange = newRange;

    return state;
  }

  _removeItems (state: ItemsState<Item | null, ExtraData>, ids: string[]): ItemsState<Item | null, ExtraData> {
    if (ids == null) {
      return state;
    }

    const newSelection: Immutable.Set<string> = state.selection.filter(
      (id: string) => !ids.includes(id)
    );

    const newItems: Immutable.List<Item | null> = state.items.filter(
      (item: Item | null) => (
        (item == null) ||
        !ids.includes(item.id)
      )
    );

    let mappedItems: Immutable.Map<string, Item> = state.mappedItems;
    for (const id of ids) {
      mappedItems = mappedItems.delete(id);
    }

    if (newItems.size !== state.items.size) {
      return state
        .set('items', newItems)
        .set('selection', newSelection)
        .set('mappedItems', mappedItems);
    }

    return state;
  }

  compareValues (firstParameter: unknown, secondParameter: unknown): number {
    return compareValues(firstParameter, secondParameter);
  }

  _compare (firstParameter: unknown, secondParameter: unknown, ascending: boolean): number {
    const compareResult: number = this.compareValues(firstParameter, secondParameter);
    return (ascending ? compareResult : -compareResult);
  }

  _getCompareFunction (sortOrder: SortOrder): ((a: Item, b: Item) => number) | null {
    const sortKeyword: string = this.getItemKeyForKeyword(sortOrder.keyword) || sortOrder.keyword;
    const secondaryKeyword: string = this.getItemKeyForKeyword(this.getSecondarySortKeyword()) || this.getSecondarySortKeyword();
    const preSortOrder: string | SortOrder | null = this._getPreSortOrder();

    let optionalPresortKeyword: string | null = null;
    let presortAscending: boolean = true;
    if (preSortOrder instanceof SortOrder) {
      optionalPresortKeyword = preSortOrder.keyword;
      presortAscending = preSortOrder.ascending;
    } else if (typeof preSortOrder === 'string') {
      optionalPresortKeyword = preSortOrder;
      presortAscending = sortOrder.ascending;
    }

    return (firstTemplate: Item, secondTemplate: Item) => {
      let compareResult: number = 0;
      if (optionalPresortKeyword != null && optionalPresortKeyword !== sortKeyword) {
        const primaryKeyword: string = optionalPresortKeyword;
        const firstValue = this.getValue(firstTemplate, primaryKeyword);
        const secondValue = this.getValue(secondTemplate, primaryKeyword);

        compareResult = this._compare(firstValue, secondValue, presortAscending);
      }

      if (compareResult === 0) {
        const firstValue = this.getValue(firstTemplate, sortKeyword);
        const secondValue = this.getValue(secondTemplate, sortKeyword);

        compareResult = this._compare(firstValue, secondValue, sortOrder.ascending);

        if (compareResult === 0 && sortKeyword !== secondaryKeyword) {
          const firstSecondaryValue = this.getValue(firstTemplate, secondaryKeyword);
          const secondSecondaryValue = this.getValue(secondTemplate, secondaryKeyword);

          compareResult = this._compare(firstSecondaryValue, secondSecondaryValue, true);
        }
      }

      return compareResult;
    };
  }

  _getInsertPosition (item: Item, items: Immutable.List<Item | null>, currentRange: RangeType, sortOrder: SortOrder): number | undefined {
    const itemKey: string | null = this.getItemKeyForKeyword(sortOrder.keyword);

    if (itemKey) {
      const comparefunction: ((a: Item, b: Item) => number) | null = this._getCompareFunction(sortOrder);

      if (comparefunction != null) {
        const _comparefunction: (a: Item, b: Item) => number = comparefunction;

        let itemIndex: number = currentRange.from;

        for (; itemIndex < currentRange.until; itemIndex++) {
          const currentItem: Item | null | undefined = items.get(itemIndex);
          if (currentItem != null) {
            const _currentItem: Item = currentItem;
            if (_comparefunction(item, _currentItem) < 0) {
              break;
            }
          }
        }

        if ((itemIndex > currentRange.from || currentRange.from === 0) &&
        (itemIndex < currentRange.until || currentRange.until === items.size)) {
          return itemIndex;
        }
      }
    }
  }

  getValue (item: Item, itemKey: string) {
    return item.get(itemKey);
  }

  _compareToGroupName (itemValue: unknown, groupName: string | null): number {
    if (itemValue == null) {
      if (groupName == null) {
        return 0; // null == null
      }
      return -1; // null < groupName
    } else if (groupName == null) {
      return 1; // itemValue > null
    }
    if (typeof itemValue ==='number' && !isNaN(itemValue)) {
      const groupNumber: number = parseInt(groupName);
      return (itemValue < groupNumber ? -1 : (itemValue === groupNumber ? 0 : 1));
    }
    return this.compareValues(itemValue.toString(), groupName);
  }

  _updateGrouping (state: ItemsState<Item | null, ExtraData>, oldItem: Item | null, newItem: Item): ItemsState<Item | null, ExtraData> {
    if (this.getGroupingKeywords().includes(state.sortOrder.keyword)) {
      const itemKey: string | null = this.getItemKeyForKeyword(state.sortOrder.keyword);

      if (itemKey) {
        const oldValue = oldItem ? oldItem.get(itemKey) : null;
        const newValue = newItem.get(itemKey);

        if (!oldValue || oldValue !== newValue) {
          if (oldValue || oldItem) {
            const oldValueCountIndex: number = state.countPerGroup.findIndex((countAndGroup) => this._compareToGroupName(oldValue, countAndGroup.name) === 0);

            if (oldValueCountIndex !== -1) {
              const oldGroupCount: number = state.countPerGroup.get(oldValueCountIndex)?.count ?? 0;
              if (oldGroupCount - 1 <= 0) {
                state = state.set('countPerGroup', state.countPerGroup.remove(oldValueCountIndex));
              } else {
                state = state.set('countPerGroup', state.countPerGroup.setIn([oldValueCountIndex, 'count'], oldGroupCount - 1));
              }
            }
          }

          const newValueCountIndex: number = state.countPerGroup.findIndex((countAndGroup) => this._compareToGroupName(newValue, countAndGroup.name) === 0);

          if (newValueCountIndex === -1) {
            if (state.sortOrder.ascending) {
              let groupIndex: number = 0;

              for (; groupIndex < state.countPerGroup.size; groupIndex++) {
                const groupName: string = state.countPerGroup.get(groupIndex)?.name ?? '';

                if (!newValue || this._compareToGroupName(newValue, groupName) < 0) {
                  break;
                }
              }

              const newGroup: GroupAndCount = new GroupAndCount({name: newValue, count: 1});
              if (groupIndex < state.countPerGroup.size) {
                state = state.set('countPerGroup', state.countPerGroup.insert(groupIndex, newGroup));
              } else {
                state = state.set('countPerGroup', state.countPerGroup.push(newGroup));
              }
            } else {
              let groupIndex = state.countPerGroup.size - 1;

              for (; groupIndex >= 0; groupIndex--) {
                const groupName: string = state.countPerGroup.get(groupIndex)?.name ?? '';

                if (newValue && this._compareToGroupName(newValue, groupName) < 0) {
                  // the new group's name comes before (lexicographical order) the group's name at groupIndex
                  break;
                }
              }

              const newGroup: GroupAndCount = new GroupAndCount({name: newValue, count: 1});
              // the new group's name comes before (lexicographical order) the group name at groupIndex
              // since the sort order is descending the new group needs to be inserted after the group at groupIndex
              state = state.set('countPerGroup', state.countPerGroup.insert(groupIndex + 1, newGroup));
            }
          } else {
            const newGroupCount: number = state.countPerGroup.get(newValueCountIndex)?.count ?? 0;
            state = state.set('countPerGroup', state.countPerGroup.setIn([newValueCountIndex, 'count'], newGroupCount + 1));
          }
        }
      }
    }
    return state;
  }

  _updateItem (state: ItemsState<Item | null, ExtraData>, newItem: Item): {state: ItemsState<Item | null, ExtraData>, shouldFetch: boolean} {
    let updatedState: ItemsState<Item | null, ExtraData> = state;

    const indexToUpdate: number = this._findIndexForId(updatedState.items, newItem.id);
    const checker = new FilterChecker(this._getAllFilterKeywords(), this.getItemKeyForKeyword, this.getStateNames());

    let shouldFetch: boolean = false;

    if (indexToUpdate !== -1) {
      if (!Immutable.is(updatedState.items.get(indexToUpdate), newItem)) {
        if (checker.itemConformsToFilters(newItem, updatedState.filter, updatedState.advancedFilter)) {
          // item needs to be updated, find out if it needs to be replaced or added on another position
          const insertPosition: number | undefined = this._getInsertPosition(newItem, updatedState.items, this._currentRange, updatedState.sortOrder);

          if (insertPosition != null) {
            updatedState = this._updateGrouping(updatedState, indexToUpdate !== -1 ? updatedState.items.get(indexToUpdate) ?? null : null, newItem);

            if (insertPosition === -1) {
              updatedState = updatedState.set('items', updatedState.items.set(indexToUpdate, newItem));
            } else {
              updatedState = updatedState.set('items', state.items.remove(indexToUpdate));

              const newInsertPosition: number | undefined = this._getInsertPosition(newItem, updatedState.items, this._currentRange, updatedState.sortOrder);

              if (newInsertPosition != null) {
                if (newInsertPosition < this._currentRange.from) {
                  updatedState = updatedState.set('items', updatedState.items.insert(0, null));
                } else if (newInsertPosition > this._currentRange.until) {
                  updatedState = updatedState.set('items', updatedState.items.push(null));
                } else {
                  updatedState = updatedState.set('items', updatedState.items.insert(newInsertPosition, newItem));
                }
              }
            }
          } else {
            shouldFetch = true;
          }
        } else {
          updatedState = updatedState.set('items', updatedState.items.remove(indexToUpdate));

          if (updatedState.selection.includes(newItem.id)) {
            updatedState = updatedState.set('selection', updatedState.selection.remove(newItem.id));
          }
        }
      } // else do nothing, item is not changed
    } else {
      if (checker.itemConformsToFilters(newItem, updatedState.filter, updatedState.advancedFilter)) {
        // item needs to be added, find where it needs to be inserted
        const insertPosition: number | undefined = this._getInsertPosition(newItem, updatedState.items, this._currentRange, updatedState.sortOrder);

        if (insertPosition != null) {
          updatedState = this._updateGrouping(updatedState, indexToUpdate !== -1 ? updatedState.items.get(indexToUpdate) ?? null : null, newItem);

          if (insertPosition === -1) {
            shouldFetch = true;
          } else if (insertPosition < this._currentRange.from) {
            updatedState = updatedState.set('items', updatedState.items.insert(0, null));
          } else if (insertPosition > this._currentRange.until) {
            updatedState = updatedState.set('items', updatedState.items.push(null));
          } else {
            updatedState = updatedState.set('items', updatedState.items.insert(insertPosition, newItem));
          }
        } else {
          const mappedNewItem = updatedState.mappedItems.get(newItem.id);
          if (!Immutable.is(mappedNewItem, newItem)) {
            shouldFetch = true;
          }
        }
      } // else do nothing, item is not changed
    }

    if (!Immutable.is(updatedState.mappedItems.get(newItem.id), newItem)) {
      updatedState = updatedState
        .set('mappedItems', updatedState.mappedItems
          .set(newItem.id, newItem)
      );

      // check if added item equals mappedItem
      const index: number = this._findIndexForId(updatedState.items, newItem.id);
      if (index !== -1) {
        if (updatedState.mappedItems.get(newItem.id) !== newItem) {
          console.log('item doesn\'t match mapped item');
        }
      }
    }

    return {state: updatedState, shouldFetch: shouldFetch};
  }

  _updateItems (state: ItemsState<Item | null, ExtraData>, actions: Immutable.List<UpdateAction>): ItemsState<Item | null, ExtraData> {
    let shouldFetch: boolean = false;

    let updatedState: ItemsState<Item | null, ExtraData> = state;

    const idsToRemove: string[] = [];
    const itemsToAddOrUpdate: Item[] = [];

    for (const updateAction of actions) {
      if (updateAction.action === 'added') {
        itemsToAddOrUpdate.push(...updateAction.items);
      } else if (updateAction.action === 'modified') {
        itemsToAddOrUpdate.push(...updateAction.items);
      } else if (updateAction.action === 'removed') {
        idsToRemove.push(...updateAction.ids.filter((id: string) => !this._removedIds.includes(id)));
        this._removedIds = this._removedIds.filter((id: string) => updateAction.ids.includes(id));
      }
    }

    for (const itemToAddOrUpdate of itemsToAddOrUpdate) {
      if (itemToAddOrUpdate) {
        const result: {state: ItemsState<Item | null, ExtraData>, shouldFetch: boolean} = this._updateItem(updatedState, itemToAddOrUpdate);
        updatedState = result.state;
        if (result.shouldFetch) {
          shouldFetch = true;
        }
      }
    }

    if (idsToRemove.length > 0) {
      updatedState = this._removeItems(updatedState, idsToRemove);
      shouldFetch = true;
    }

    if (shouldFetch && this._isStoreActive) {
      updatedState = this._getRange(
        updatedState,
        this._currentRange,
        updatedState.filter,
        updatedState.advancedFilter,
        updatedState.sortOrder
      );
    }

    return updatedState;
  }

  _isItemInUse (id: string): boolean {
    return false;
  }

  _isSelected (item: Item | null, selection: Immutable.Set<string>): boolean {
    return item != null && selection.includes(item.id);
  }

  _matchesId (item: Item | null, id: string): boolean {
    return item != null && item.id === id;
  }

  _findIndexForId (items: Immutable.List<Item | null>, id: string): number {
    return items.findIndex((item: Item | null) => this._matchesId(item, id));
  }

  _getUpdatedSelectionIndexes (
    items: Immutable.List<Item | null>,
    filter: Filter,
    advancedFilter: Filter,
    sortOrder: SortOrder,
    selection: Immutable.Set<string>,
    askServerIfNeeded: boolean = true,
    ensureSelectionIsVisible: boolean = true
  ): Immutable.Map<string, number> {
    // determine selection indexes
    let selectionIndexes: Immutable.Map<string, number> = Immutable.Map();
    let indexFound = true;
    for (const id of selection) {
      const index = this._findIndexForId(items, id);
      if (index === -1) {
        indexFound = false;
      }
      selectionIndexes = selectionIndexes.set(id, index);
    }

    if (askServerIfNeeded && !selection.isEmpty() && !indexFound) {
      if (this.Actions.find != null) {
        if (ensureSelectionIsVisible) {
          this._shouldSetEnsureRangeVisible = true;
        }

        let groupBy: SortOrder = sortOrder;
        if (!this.getGroupingKeywords().includes(sortOrder.keyword)) {
          groupBy = new SortOrder({keyword: '', ascending: true});
        }

        if (this._isStoreActive) {
          this._find(selection, filter, advancedFilter, groupBy, sortOrder);
        }
      }
    }

    return selectionIndexes;
  }

  _setup (state: ItemsState<Item | null, ExtraData>, selection: Immutable.Set<string>, advancedFilter: Filter, sortOrder: SortOrder,
          disabledForSelection: Immutable.List<string>, allowSelectionsFromAllGroups: boolean): ItemsState<Item | null, ExtraData> {
    state = state.set('selection', selection)
      .set('filter', Filter.createEmpty()) // clear the filter
      .set('filterString', '')
      .set('advancedFilter', advancedFilter)
      .set('sortOrder', sortOrder)
      .set('disabledForSelection', disabledForSelection)
      .set('allowMultiSelect', true)
      .set('allowSelectionsFromAllGroups', allowSelectionsFromAllGroups);
    state = state.set('hasNotFetchedYet', true);
    if (this._isStoreActive) {
      state = this._getRange(
        state,
        this._currentRange,
        state.filter,
        state.advancedFilter,
        state.sortOrder);
    }
    return this._removeMappedItemsThatAreNotInUseByOtherStores(state.set('items', state.items.map(() => null)));
  }

  _setSortOrder (state: ItemsState<Item | null, ExtraData>, newSortOrder: SortOrder): ItemsState<Item | null, ExtraData> {
    this._shouldSetEnsureRangeVisible = false;
    if (!Immutable.is(state.sortOrder, newSortOrder)) {
      state = state.set('hasNotFetchedYet', true);
      if (this._isStoreActive) {
        state = this._getRange(
          state,
          this._currentRange,
          state.filter,
          state.advancedFilter,
          newSortOrder
        );
      }

      return state.set('items', state.items.map(() => null)).set('sortOrder', newSortOrder);
    }
    return state;
  }

  _hasListeners (): boolean {
    return this.__emitter.listeners(this.__changeEvent).length > 0;
  }

  override reduce (state: ItemsState<Item | null, ExtraData>, action: ActionType): ItemsState<Item | null, ExtraData> {
    const hasListeners: boolean = this._hasListeners();

    if (!hasListeners && this._isStoreActive) {
      this._isStoreDirty = true;
    }

    this._isStoreActive = hasListeners;

    // handle actions if store is active
    switch (action.actionType) {
      case undefined: {
        throw Error('Action is undefined.');
      }
      case AllItemsConstants.REFRESH: {
        if (this._isStoreActive) {
          this._shouldSetEnsureRangeVisible = false;
          return this._refresh(state);
        }
        return state;
      }
      case this.Constants.SET_FILTER: {
        this._shouldSetEnsureRangeVisible = false;

        if (action.filterMode === FilterMode.GALLERY) {
          if (!Immutable.is(state.filterGallery, action.filter)) {
            return state
             .set('filterGallery', action.filter)
             .set('filterStringGallery', action.filterString);
          } else if (action.filterString !== state.filterString) {
            return state.set('filterStringGallery', action.filterString);
          }
        } else if (action.filterMode === FilterMode.TEMP) {
          if (!Immutable.is(state.filterTemp, action.filter)) {
            return state
             .set('filterTemp', action.filter)
             .set('filterStringTemp', action.filterString);
          } else if (action.filterString !== state.filterString) {
            return state.set('filterStringTemp', action.filterString);
          }
        }

        return state;
      }
      case this.Constants.ACTIVATE_FILTER: {
        if (this._isStoreActive) {
          let filter: Filter = new Filter();
          let filterString: string = '';

          if (action.filterMode === FilterMode.GALLERY) {
            filter = state.filterGallery;
            filterString = state.filterStringGallery;
          } else if (action.filterMode === FilterMode.TEMP) {
            filter = state.filterTemp;
            filterString = state.filterStringTemp;
          }

          if (!Immutable.is(state.filter, filter)) {
            return this._setFilter(state, filter, filterString);
          }
        }

        return state;
      }
      case this.Constants.SET_ADVANCED_FILTER: {
        if (this._isStoreActive) {
          this._shouldSetEnsureRangeVisible = false;
          return this._setAdvancedFilter(state, action.advancedFilter);
        } else {
          // set new advanced filter without fetching data from server
        }

        return state;
      }
      case this.Constants.SET_VISIBLE_RANGE: {
        if (this._isStoreActive) {
          this._shouldSetEnsureRangeVisible = false;
          let stateUpdatedWithRange = this._updateRange(state, action.range, state.hasNotFetchedYet);
          if (stateUpdatedWithRange.ensureSelectionIsVisible) {
            stateUpdatedWithRange = stateUpdatedWithRange.set('ensureSelectionIsVisible', false);
          }
          return stateUpdatedWithRange;
        } else {
          // set visible range without fetching data from server
        }

        return state;
      }
      case this.Constants.SET_SORTORDER: {
        return this._setSortOrder(state, action.sortOrder);
      }
      case this.Constants.OPEN:
      case this.Constants.GET_FOR_OPEN_SUCCESS: {
        if (action.item != null) {
          // when opening an item...
          // add the item if not present
          if (!state.mappedItems.has(action.item.id)) {
            state = state.set('mappedItems', state.mappedItems
              .set(action.item.id, action.item)
            );
          }
          // set it as selected
          const selection = Immutable.Set([action.item.id]);
          let updatedSelectionIndexes = state.selectionIndexes;
          if (this._isStoreActive) {
            updatedSelectionIndexes = this._getUpdatedSelectionIndexes(
              state.items,
              state.filter,
              state.advancedFilter,
              state.sortOrder,
              selection,
              false
            );
          }

          return state
            .set('selection', selection)
            .set('selectionIndexes', updatedSelectionIndexes);
        }

        return state;
      }
      case this.Constants.SAVE_SUCCESS: {
        // set the new item as selected
        const newSelection: Immutable.Set<string> = Immutable.Set([action.item.id]);
        const newState: ItemsState<Item | null, ExtraData> = state
          .set('selection', newSelection);

        // update item if present
        const stateAndShouldFetch = this._updateItem(newState, action.item);

        if (this._isStoreActive) {
          if (stateAndShouldFetch.shouldFetch) {
            stateAndShouldFetch.state = this._getRange(
              stateAndShouldFetch.state,
              this._currentRange,
              stateAndShouldFetch.state.filter,
              stateAndShouldFetch.state.advancedFilter,
              stateAndShouldFetch.state.sortOrder
            );
          } else {
            // fetch missing in range
            this._updateRange(stateAndShouldFetch.state, this._currentRange, false);
          }

          const selectionIndexes = this._getUpdatedSelectionIndexes(state.items, state.filter, state.advancedFilter, state.sortOrder, newSelection);
          return stateAndShouldFetch.state
            .set('ensureSelectionIsVisible', true)
            .set('selectionIndexes', selectionIndexes);
        }

        return stateAndShouldFetch.state
          .set('ensureSelectionIsVisible', true)
          .set('selectionIndexes', state.selectionIndexes.clear());
      }
      case this.Constants.REMOVE_SUCCESS: {
        this._removedIds.push(...action.ids);
        let stateAfterRemove: ItemsState<Item | null, ExtraData> = this._removeItems(state, action.ids);

        if (this._isStoreActive) {
          stateAfterRemove = this._getRange(
            stateAfterRemove,
            this._currentRange,
            state.filter,
            state.advancedFilter,
            state.sortOrder
          );
        }

        return stateAfterRemove;
      }
      case this.Constants.UPDATE: {
        return this._updateItems(state, action.actions);
      }
      case this.Constants.SET_SELECTION: {
        if (this._isStoreActive) {
          const selectionIndexes = this._getUpdatedSelectionIndexes(state.items, state.filter, state.advancedFilter, state.sortOrder, action.selection);

          // ensure the new selection's items are in the store
          if (this.Actions.getById != null) {
            const itemsToGetFromServer = [];
            for (const id of action.selection) {
              if (this.getItem(id) == null) {
                itemsToGetFromServer.push(id);
              }
            }

            if (itemsToGetFromServer.length > 0) {
              this.Actions.getById(itemsToGetFromServer);
            }
          }

          return state
            .set('selection', action.selection)
            .set('ensureSelectionIsVisible', !!action.ensureSelectionIsVisible && action.selection.size === 1)
            .set('selectionIndexes', selectionIndexes);
        }

        return state
          .set('selection', action.selection)
          .set('ensureSelectionIsVisible', !!action.ensureSelectionIsVisible && action.selection.size === 1)
          .set('selectionIndexes', state.selectionIndexes.clear());
      }
      case this.Constants.MODIFY_SELECTION: {
        if (!action.id.startsWith('tile')) {
          if (state.allowMultiSelect) {
            const newSelection: Immutable.Set<string> = (state.selection.includes(action.id)
              ? state.selection.delete(action.id)
              : state.selection.add(action.id)
            );

            if (this._isStoreActive) {
              const selectionIndexes: Immutable.Map<string, number> = this._getUpdatedSelectionIndexes(state.items, state.filter, state.advancedFilter, state.sortOrder, newSelection);

              return state
                .set('selection', newSelection)
                .set('selectionIndexes', selectionIndexes)
                .set('ensureSelectionIsVisible', false);
            } else {
              return state
                .set('selection', newSelection)
                .set('selectionIndexes', state.selectionIndexes.clear())
                .set('ensureSelectionIsVisible', false);
            }
          } else if (state.selection.includes(action.id)) {
            return state
              .set('selection', Immutable.Set())
              .set('selectionIndexes', Immutable.Map())
              .set('ensureSelectionIsVisible', false);
          } else {
            if (this._isStoreActive) {
              // ensure the new selection's items are in the store
              if ((this.getItem(action.id) == null) &&
                (this.Actions.getById != null)) {
                this.Actions.getById([action.id]);
              }

              const newSelection: Immutable.Set<string> = Immutable.Set([action.id]);
              const selectionIndexes: Immutable.Map<string, number> = this._getUpdatedSelectionIndexes(state.items, state.filter, state.advancedFilter, state.sortOrder, newSelection);

              return state
                .set('selection', newSelection)
                .set('selectionIndexes', selectionIndexes)
                .set('ensureSelectionIsVisible', !!action.ensureSelectionIsVisible);
            } else {
              const newSelection: Immutable.Set<string> = Immutable.Set([action.id]);

              return state
                .set('selection', newSelection)
                .set('selectionIndexes', state.selectionIndexes.clear())
                .set('ensureSelectionIsVisible', !!action.ensureSelectionIsVisible);
            }
          }
        }
        return state;
      }
      case this.Constants.ALLOW_MULTI_SELECT: {
        return state.set('allowMultiSelect', action.allow);
      }
      case this.Constants.ENSURE_SELECTION_IS_VISIBLE: {
        return state.set(
          'ensureSelectionIsVisible',
          action.ensureSelectionIsVisible
        );
      }
      case this.Constants.GET_BY_ID_SUCCESS: {
        let mappedItems: Immutable.Map<string, Item> = state.mappedItems;
        for (const itemToAdd of action.items) {
          mappedItems = mappedItems.set(itemToAdd.id, itemToAdd);
        }
        return state.set('mappedItems', mappedItems);
      }
      case this.Constants.GET_SUCCESS: {
        if (Immutable.is(action.filter, state.filter) && Immutable.is(action.advancedFilter, state.advancedFilter) && Immutable.is(action.sortOrder, state.sortOrder)) {
          const mappedItemsToAdd: Item[] = [];

          const selection = state.selection;

          const mergedList: Immutable.List<Item | null> = state.items.setSize(action.totalCount).map((oldItem: Item | null, itemIndex: number) => {
            let newItem: Item | null = null;
            if (itemIndex >= action.range.from && itemIndex < action.range.until) {
              const newItemIndex: number = itemIndex - action.range.from;

              newItem = action.items.get(newItemIndex);
            }

            if (itemIndex >= this._currentRange.from && itemIndex < this._currentRange.until) {
              if (newItem != null) {
                if (oldItem == null || !Immutable.is(newItem, oldItem)) {
                  mappedItemsToAdd.push(newItem);

                  return newItem;
                } else {
                  return oldItem;
                }
              } else if (oldItem != null && action.items.findIndex((item: Item) => item != null && item.id === oldItem.id) === -1) {
                return oldItem;
              } else {
                return null;
              }
            } else {
              if (newItem != null && this._isSelected(newItem, selection)) {
                if (oldItem == null || !Immutable.is(newItem, oldItem)) {
                  mappedItemsToAdd.push(newItem);

                  return newItem;
                } else {
                  return oldItem;
                }
              }
            }
            
            return null;
          });

          let mappedItems: Immutable.Map<string, Item> = state.mappedItems;
          for (const mappedItemToAdd of mappedItemsToAdd) {
            mappedItems = mappedItems.set(mappedItemToAdd.id, mappedItemToAdd);
          }

          // find first the id's of defined elements in mergedList
          const tempItemIds: string[] = [];
          for (const tempItem of mergedList) {
            if (tempItem) {
              tempItemIds.push(tempItem.id);
            }
          }

          const mappedItemIdsToRemove: string[] = [];
          for (const mappedItemKeyValue of mappedItems) {
            if (typeof mappedItemKeyValue[0] === 'string') {
              const mappedItemId: string = mappedItemKeyValue[0];
              const mappedItem: Item = mappedItemKeyValue[1];
              if (!tempItemIds.includes(mappedItemId) &&
                !this._isSelected(mappedItem, selection) &&
                !this._isItemInUse(mappedItemId)) {
                mappedItemIdsToRemove.push(mappedItemId);
              }
            }
          }

          for (const mappedIdToRemove of mappedItemIdsToRemove) {
            mappedItems = mappedItems.delete(mappedIdToRemove);
          }

          if (this._isStoreActive) {
            this._isStoreDirty = false;

            const rangeToFetch: RangeType | null = this._calculateRangeToGet(mergedList, this._currentRange, state.hasNotFetchedYet);

            state = state.set('isFetching', false);
            this._isFetching = false;
            if (rangeToFetch != null && ((rangeToFetch.from !== action.range.from && rangeToFetch.until !== action.range.until) || this._refetchCount < 2)) {
              this._refetchCount++;
              if (rangeToFetch) {
                state = this._getRange(
                  state,
                  rangeToFetch,
                  state.filter,
                  state.advancedFilter,
                  state.sortOrder
                );
              }
            } else {
              this._refetchCount = 0;
            }

            // adapt selection
            const selectionExistsInItems = (id: string) => {
              return this._selectionExistsInItems(id, mergedList);
            };

            const newSelection: Immutable.Set<string> = state.selection.filter(selectionExistsInItems);
            const selectionIndexes: Immutable.Map<string, number> = this._getUpdatedSelectionIndexes(
              mergedList,
              state.filter,
              state.advancedFilter,
              state.sortOrder,
              selection,
              true,
              false
            );

            return state
              .set('items', mergedList)
              .set('countPerGroup', action.countPerGroup)
              .set('mappedItems', mappedItems)
              .set('selection', newSelection)
              .set('selectionIndexes', selectionIndexes)
              .set('hasReceivedInitialData', true);
          } else {
            state = state.set('isFetching', false);
            this._isFetching = false;
            this._refetchCount = 0;

            // adapt selection
            const selectionExistsInItems = (id: string) => {
              return this._selectionExistsInItems(id, mergedList);
            };

            const newSelection: Immutable.Set<string> = state.selection.filter(selectionExistsInItems);

            return state
              .set('items',mergedList)
              .set('countPerGroup', action.countPerGroup)
              .set('mappedItems', mappedItems)
              .set('selection', newSelection)
              .set('selectionIndexes', state.selectionIndexes.clear())
              .set('hasReceivedInitialData', false);
          }
        } else if (this._isStoreActive) {
          const rangeToFetch: RangeType | null = this._calculateRangeToGet(state.items, this._currentRange, state.hasNotFetchedYet);

          state = state.set('isFetching', false);
          this._refetchCount = 0;
          this._isFetching = false;
          if (rangeToFetch) {
            state = this._getRange(
              state,
              rangeToFetch,
              state.filter,
              state.advancedFilter,
              state.sortOrder
            );
          }

          return state.set('selectionIndexes', state.selectionIndexes.clear()).set('hasReceivedInitialData', true);
        }

        return state;
      }
      case this.Constants.GET_FAIL: {
        this._refetchCount = 0;
        if (this._isStoreActive) {
          this._isStoreDirty = false;
        }

        this._isFetching = false;

        return state.set('isFetching', false)
          .set('items', state.items.clear()).set('hasReceivedInitialData', true);
      }
      case this.Constants.FIND_SUCCESS: {
        let selectionReallyFound = false;
        for (const id of state.selection) {
          const index = action.indexes.get(id);
          if (index != null && index >= 0) {
            selectionReallyFound = true;
            break;
          }
        }

        if (selectionReallyFound) {
          return state
            .set('selectionIndexes', action.indexes)
            .set('ensureSelectionIsVisible', this._shouldSetEnsureRangeVisible);
        } else {
          return state
            .set('selection', Immutable.Set())
            .set('selectionIndexes', Immutable.Map())
            .set('ensureSelectionIsVisible', false);
        }
      }
      case UserPreferencesConstants.CLEAR_SUCCESS: {
        return this._setSortOrder(state, this.getInitialSortOrder());
      }
      default: {
        return state;
      }
    }
  }
}

export default PaginatedItemsStore;
